import axios from "axios";
import { getToken, getRefreshToken, setAccessToken, clearSession, getRoleForPath } from "./authStorage";
import { getOnline, setOnline, probeHealth } from "./networkState";

// Normally just REACT_APP_BACKEND_URL. In LAN development the configured
// host is a DHCP-assigned IP that goes stale whenever the machine changes
// network — see lib/backendUrl.js for why, and for the guards that keep
// production untouched.
import { BACKEND_URL } from "./backendUrl";

export const API_BASE = `${BACKEND_URL}/api`;

// Generous safety net against indefinite hangs (dropped connection, server
// stall) — not a UX-facing deadline. Per-call `timeout` in a request config
// still overrides this, so legitimately heavy endpoints (Excel export, bulk
// import) aren't affected.
const api = axios.create({ baseURL: BACKEND_URL, timeout: 30000 });

api.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  // Hard app-wide guarantee: don't even attempt the call while we know
  // we're offline. This is what NetworkProvider (lib/network.jsx) relies on
  // to satisfy "do not call any APIs" — enforced here, at the axios layer,
  // regardless of where the offline screen sits in the component tree.
  if (!getOnline()) {
    return Promise.reject({ __offline: true, message: "No internet connection" });
  }
  return config;
});

// Turns an Axios/API error into a short, safe string a user can actually
// read. Backend HTTPException `detail` strings are already written to be
// user-facing (e.g. "No supervisor with this phone") so those pass through
// as-is; anything else (raw network failures, a bare 401/403/500 with no
// detail, a JS exception) collapses to a generic message instead of leaking
// "AxiosError: Request failed with status code 500" or a stack trace.
export function friendlyMessage(err) {
  if (err?.__offline) return "You're offline. Check your connection and try again.";
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && detail.trim()) return detail;
  const status = err?.response?.status;
  if (status === 401) return "Your session has expired. Please log in again.";
  if (status === 403) return "You don't have permission to do that.";
  if (status === 404) return "That couldn't be found.";
  if (status && status >= 500) return "Something went wrong on our end. Please try again.";
  if (!err?.response) return "Couldn't reach the server. Please check your connection and try again.";
  return "Something went wrong. Please try again.";
}

// Silent access-token refresh. A bare `axios.post` (not the `api` instance)
// on purpose — going through `api` would re-enter this same response
// interceptor if the refresh call itself ever 401s, and it doesn't need the
// request interceptor's Authorization header (the refresh token is the
// payload, not a bearer credential). Module-level `refreshPromise` makes
// this single-flight: several requests failing around the same moment (a
// page firing 2-3 parallel calls right after a token expires) share ONE
// refresh round-trip instead of racing multiple, which would be wasteful
// and — if refresh tokens are ever rotated later — actively unsafe.
let refreshPromise = null;

function attemptRefresh() {
  const pathname = window.location.pathname;
  const role = getRoleForPath(pathname);
  const refreshToken = getRefreshToken(pathname);
  if (!refreshToken) return Promise.resolve(null);

  if (!refreshPromise) {
    refreshPromise = axios
      .post(`${BACKEND_URL}/api/auth/refresh`, { refresh_token: refreshToken }, { timeout: 15000 })
      .then(({ data }) => {
        setAccessToken(role, data.token);
        return data.token;
      })
      .catch(() => null)
      .finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
}

/** The Authorization header the failing request actually went out with.
 *  Axios v1 hands back an AxiosHeaders instance on some paths and a plain
 *  object on others (a retry we build ourselves), so read both shapes. */
function requestAuthHeader(cfg) {
  const h = cfg?.headers;
  if (!h) return null;
  if (typeof h.get === "function") return h.get("Authorization") || h.get("authorization") || null;
  return h.Authorization || h.authorization || null;
}

api.interceptors.response.use(
  (r) => r,
  async (err) => {
    // Always log the real error for debugging — friendlyMessage() is what
    // reaches the user, this is what reaches us.
    // eslint-disable-next-line no-console
    console.error("[api]", err?.config?.method?.toUpperCase(), err?.config?.url, err);
    err.userMessage = friendlyMessage(err);

    if (err.response?.status === 401) {
      const cfg = err.config || {};
      // Never try to refresh in response to the refresh call itself
      // (that refresh token is genuinely dead — fall straight through to
      // logout) or a request that's already been retried once post-refresh
      // (the new access token was rejected too, so refreshing again
      // wouldn't help and risks an infinite loop).
      const isRefreshCall = (cfg.url || "").includes("/auth/refresh");
      if (!isRefreshCall && !cfg.__isRetryAfterRefresh) {
        const newToken = await attemptRefresh();
        if (newToken) {
          const retryConfig = {
            ...cfg,
            __isRetryAfterRefresh: true,
            headers: { ...cfg.headers, Authorization: `Bearer ${newToken}` },
          };
          try {
            return await api(retryConfig);
          } catch (retryErr) {
            return Promise.reject(retryErr);
          }
        }
      }
      // No refresh token, or refresh failed (also expired/invalid) — clear
      // only this section's session and let AuthProvider (which owns the
      // router) bounce to the right login screen, instead of leaving every
      // in-flight page stuck on stale data or a silent 401.
      //
      // `hadSession` is what tells a genuine EXPIRY apart from simply not
      // being logged in. A 401 on a request that never carried a token —
      // fresh install, reinstall, cleared storage, or the guest shell
      // calling an authenticated endpoint — means there was nothing to
      // expire, so AuthProvider must not tell the user their session ended.
      // The same applies once a session is already gone (explicit logout, or
      // an earlier 401 in the same burst that cleared it): later 401s are
      // fallout, not a new expiry.
      const hadSession = !!requestAuthHeader(cfg) && !!getToken();
      if (hadSession) clearSession();
      window.dispatchEvent(new CustomEvent("auth:unauthorized", { detail: { hadSession } }));
    } else if (!err.response && !err.__offline) {
      // A request was attempted and no HTTP response reached us. That is a
      // SUSPECTED outage, not a confirmed one: a reverse proxy answering
      // with its own error page (nginx 413 "Request Entity Too Large", a
      // 502) sends no CORS headers, so the browser withholds the response
      // and axios reports a bare "Network Error" — indistinguishable here
      // from a dropped connection. Flipping to offline on that alone
      // replaced the whole app with the No-Internet screen and remounted it
      // on the next successful poll, which the user saw as "the page
      // refreshed" over a perfectly healthy connection. So confirm against
      // the backend first, exactly as NetworkProvider confirms every
      // positive signal; a genuine outage still fails the probe and flips
      // within a few seconds. Single-flight: one probe per burst of failures.
      confirmOffline();
    }
    return Promise.reject(err);
  }
);

let offlineProbe = null;
function confirmOffline() {
  if (offlineProbe) return offlineProbe;
  offlineProbe = probeHealth()
    .then((ok) => { if (!ok) setOnline(false); })
    .catch(() => setOnline(false))
    .finally(() => { offlineProbe = null; });
  return offlineProbe;
}

// Defense-in-depth: an API call somewhere in this large app inevitably
// forgets a `.catch()` from time to time. Without this, that shows up as a
// raw "Uncaught AxiosError: Request failed with status code ###" overlay
// (dev) or a silent broken screen (prod) instead of a message anyone can
// act on. This can't fix the missing UI feedback for that specific call,
// but it stops the raw error from reaching the console as an unhandled
// crash and logs it the same way the interceptor above does.
if (typeof window !== "undefined") {
  window.addEventListener("unhandledrejection", (event) => {
    const err = event.reason;
    if (err && (err.isAxiosError || err.__offline)) {
      event.preventDefault();
      // eslint-disable-next-line no-console
      console.error("[api] unhandled rejection:", err);
    }
  });
}

export default api;
